import { DEFAULT_LOCALE, type Locale, SUPPORTED_LOCALES } from '@/constants/locales';

/**
 * `?lang=` → `navigator.languages` → `en`. Pure, and writes nothing anywhere.
 *
 * Hand-rolled rather than `i18next-browser-languagedetector` (ADR-014). The plugin exists to run an
 * ordered detector list with caching against `localStorage`, cookies, and `sessionStorage` — every
 * one of which ADR-006 forbids. Configuring a plugin to not do the thing it exists to do costs more
 * than the twelve lines that replace it.
 *
 * `search` and `navigatorLanguages` are parameters rather than reads off `window`, because region-tag
 * handling is where detection actually goes wrong and this makes those cases directly testable.
 */
export function detectLocale(search: string, navigatorLanguages: readonly string[]): Locale {
  const fromQuery = normalizeLocale(new URLSearchParams(search).get('lang'));
  if (fromQuery !== null) return fromQuery;

  // `languages` (plural) rather than `language`: a phone set to Urdu with English second should get
  // English, not the default. Same result here, but for the right reason.
  for (const language of navigatorLanguages) {
    const match = normalizeLocale(language);
    if (match !== null) return match;
  }

  return DEFAULT_LOCALE;
}

/** `'pt-BR'` → `'pt'`, `'es-419'` → `'es'`, `'EN'` → `'en'`, `'ur-PK'` → `null` (unsupported). */
export function normalizeLocale(raw: string | null | undefined): Locale | null {
  if (raw === null || raw === undefined) return null;
  const base = raw.trim().toLowerCase().split('-')[0];
  if (base === undefined || base === '') return null;
  return (SUPPORTED_LOCALES as readonly string[]).includes(base) ? (base as Locale) : null;
}

/**
 * Makes a language choice survive a refresh without writing to the device.
 *
 * `replaceState` rather than a cookie or `localStorage` (ADR-006): the URL is already the app's only
 * persistence mechanism. This is the one intentional `replaceState` in the app, and it must preserve
 * the path — the path contains the session token.
 */
export function buildLocaleUrl(currentUrl: string, locale: Locale): string {
  const url = new URL(currentUrl);
  url.searchParams.set('lang', locale);
  return `${url.pathname}${url.search}${url.hash}`;
}
